import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

// Get confidence styling
export function getConfidenceStyle(level, score) {
  if (level === 'high' || score >= 0.8) {
    return { color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: CheckCircle };
  } else if (level === 'medium' || score >= 0.6) {
    return { color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/30', icon: AlertTriangle };
  } else {
    return { color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30', icon: XCircle };
  }
}

// Format currency
export function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value || 0);
}

// Format YYYY-MM-DD date string to user-friendly format (e.g., "Jan 15, 2024")
export function formatDate(dateStr) {
  if (!dateStr) return null;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return dateStr;
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Get next business day as YYYY-MM-DD string (skips weekends)
// Earliest valid ship date the CSR can request (also the seed default and the date
// input's min). Same-day cutoff (Tonya, 2026-07-20): before 2pm local on a weekday,
// today is allowed (supports the unofficial same-day-shipment cutoff / on-time reports);
// at/after 2pm, or on a weekend, it rolls to the next business day.
export function getNextBusinessDay() {
  const now = new Date();
  // Work in local date parts (not toISOString/UTC, which can shift the day in the
  // evening for US timezones and would break the "today" case).
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const isWeekday = date.getDay() >= 1 && date.getDay() <= 5;
  const beforeCutoff = now.getHours() < 14; // 2pm local
  if (!(isWeekday && beforeCutoff)) date.setDate(date.getDate() + 1);
  if (date.getDay() === 6) date.setDate(date.getDate() + 2); // Sat → Mon
  if (date.getDay() === 0) date.setDate(date.getDate() + 1); // Sun → Mon
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Format unit price with at least 4 decimal places
export function formatUnitPrice(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 5
  }).format(value || 0);
}

// Characters stripped from any CSR free-text (notes, comments) before it enters an order.
// NOT about XML validity — the cXML builder (ElementTree) escapes & < > correctly. This is
// defense-in-depth against Delta field-mapping breakage AND SQL-injection-style input.
// Pass maxLen to also cap the length — A+ field widths vary, so the caller supplies the limit.
//
// Set confirmed with Trey (2026-07-08): keep the special chars blocked (incl. comma and &,
// which CSRs asked for) and ALSO block ( ) ; and the -- SQL-comment sequence — the classic
// "ITEMA'); DROP TABLE Orders;--" tokens. Mirror of the backend _ERP_FORBIDDEN_CHARS in
// po-processor-app-function/services/erp_submission_service.py — keep the two in sync.
const ERP_FORBIDDEN_CHARS = /[#"'&%|!=,<>();]/g;
// Runs of 2+ hyphens (the SQL line-comment "--"); single hyphens are left alone so item
// numbers like 10-45140 still work in free-text.
const ERP_FORBIDDEN_SEQ = /-{2,}/g;

export function sanitizeErpText(value, maxLen = Infinity) {
  return String(value ?? '')
    .replace(ERP_FORBIDDEN_CHARS, '')
    .replace(ERP_FORBIDDEN_SEQ, '')
    .slice(0, maxLen);
}

// A PDF often arrives with a wrong/generic MIME type (e.g. Crystal Reports exports as
// application/octet-stream), so treat a .pdf extension as authoritative too.
export function isPdfFile(name, type) {
  return type === 'application/pdf' || String(name || '').toLowerCase().endsWith('.pdf');
}

// ITEMBAL setup is TRI-STATE: true (set up) / false (genuinely not set up) / null (couldn't
// determine). The backend sends `in_itembal: null` when the ITBAL lookup itself failed — pool
// down, query timeout, or no warehouse resolved (a non-numeric ship-to like 'HA' made
// sp_GetItemInventory throw SQL 245, emptying inventory for a whole customer). `null` must NOT
// collapse to `false`: false renders as red "NOT SET UP IN ITEMBAL" and hard-blocks submit, so a
// swallowed SQL error would tell the CSR a definitive lie about master data and cost them the
// order. Letting an unknown line through is safe — the submit-time guard
// (erp_submission_service._check_itembal) re-queries ITBAL and still rejects for real.
//
// Lives here rather than in App.jsx because LineItems/ItemSearchModal need it too and importing
// from App.jsx would be circular.
//   raw === undefined => field absent (older backend / ?mock=1 fixture); keep the legacy
//   `!!inventory` behavior rather than newly permitting everything.
export function deriveInItembal(raw, inventory) {
  if (raw === true || raw === false) return raw;
  if (inventory) return true;              // stock on hand proves an ITBAL row exists
  return raw === null ? null : false;      // null = unknown; undefined = legacy absent
}
